import { useQuery } from '@tanstack/react-query';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { cccdAdmin } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { LoadState } from "@/components/ui/LoadState"

export default function CCCDFunnelStatsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['cccd-team-funnel-stats'],
    queryFn: () => cccdAdmin.getTeamFunnelStats(),
  });

  const durations = data?.statusFunnelDurations ?? [];
  const chartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: true, formatter: (val) => `${val}d` },
    xaxis: { categories: durations.map((d) => d.status) },
    colors: ['#465fff'],
  };
  const chartSeries = [{ name: 'Días promedio', data: durations.map((d) => d.averageDaysInStatus) }];

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, 'CCCD', 'Estadísticas de embudo']}
      title="Estadísticas de embudo"
      subtitle="Dónde se atascan los equipos, no cuántos hay"
      tabs={[
        { label: 'Panel', to: '/cccd' },
        { label: 'Estadísticas de embudo', to: '/cccd/funnel-stats', active: true },
      ]}
      metrics={[
        { label: 'Miembros con datos incompletos', value: String(data?.incompleteMemberData.length ?? '—'), tone: 'warning' },
        { label: 'Ediciones con instituciones nuevas', value: String(data?.institutionParticipation.filter((e) => e.newInstitutionsCount > 0).length ?? '—') },
      ]}
    >
      {isLoading && <LoadState />}
      {!isLoading && data && (
        <div className="space-y-6">
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Tiempo promedio por estado del embudo
            </h3>
            {durations.length === 0 ? (
              <p className="text-sm text-gray-500">Sin historial de estados todavía.</p>
            ) : (
              <div className="max-w-full overflow-x-auto custom-scrollbar">
                <div className="min-w-[500px]">
                  <Chart options={chartOptions} series={chartSeries} type="bar" height={Math.max(180, durations.length * 40)} />
                </div>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Miembros con datos o consentimientos incompletos
            </h3>
            {data.incompleteMemberData.length === 0 ? (
              <p className="text-sm text-gray-500">Todos los equipos activos tienen datos completos.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.incompleteMemberData.map((m) => (
                  <li key={m.teamMemberId} className="flex justify-between">
                    <span>{m.memberName} — {m.teamName}</span>
                    <span className="text-gray-500">{m.missingFields.join(', ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Instituciones nuevas vs. recurrentes por edición
            </h3>
            {data.institutionParticipation.length === 0 ? (
              <p className="text-sm text-gray-500">Sin equipos con institución asignada todavía.</p>
            ) : (
              <ul className="space-y-3 text-sm">
                {data.institutionParticipation.map((e) => (
                  <li key={e.editionId}>
                    <p className="font-medium text-gray-900 dark:text-white">{e.editionName}</p>
                    <p className="text-gray-500">
                      {e.newInstitutionsCount} nueva(s), {e.recurrentInstitutionsCount} recurrente(s)
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </ModuleLayout>
  )
}
